import SupportedLanguages from '../../setup/intl/enums';

export type LegalDoc = 'privacy' | 'terms';

interface LegalSection {
  heading: string;
  body: string[];
}

export interface LegalContent {
  title: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
}

const EFFECTIVE_DATE_EN = '1 June 2026';
const EFFECTIVE_DATE_AR = '1 حزيران 2026';

const privacyEn: LegalContent = {
  title: 'Privacy Policy',
  updated: `Last updated: ${EFFECTIVE_DATE_EN}`,
  intro:
    'Sandoog ("Sandoog", "we", "us") operates Sandoog Center, a fulfillment platform that helps businesses in Iraq run their orders, warehouse and payouts. This policy explains what information we collect, how we use it, and the choices you have.',
  sections: [
    {
      heading: '1. Information we collect',
      body: [
        'Account and business details you provide when registering — such as your company name, contact information, entities and the users you add to your workspace.',
        'Operational data you create or upload while using the platform, including orders, products, inventory, delivery addresses and the customer information needed to fulfill those orders.',
        'Technical and usage data generated automatically, such as log data, device and browser information, and how you interact with the dashboard, which we use to keep the service secure and reliable.',
      ],
    },
    {
      heading: '2. How we use information',
      body: [
        'To operate the platform: process and fulfill orders, manage your warehouse, generate shipping labels, and calculate settlements and payouts.',
        'To provide support, communicate service updates, prevent fraud and abuse, secure the platform, and comply with our legal obligations.',
        'To improve and develop new features based on aggregated, de-identified usage trends.',
      ],
    },
    {
      heading: '3. Customer data you provide',
      body: [
        'When you upload information about your own customers (names, phone numbers, addresses), you remain responsible for that data and for having a lawful basis to share it with us.',
        'Sandoog processes that information only to deliver the fulfillment services you request — for example, to dispatch and deliver orders.',
      ],
    },
    {
      heading: '4. Sharing and disclosure',
      body: [
        'We share data with delivery and logistics partners, payment and settlement providers, and infrastructure vendors strictly to deliver the service.',
        'We may disclose information when required by law or to protect the rights, safety and security of Sandoog, our clients and their customers. We do not sell your data.',
      ],
    },
    {
      heading: '5. Data retention and security',
      body: [
        'We retain information for as long as your account is active and as needed to provide the service, resolve disputes and meet legal and accounting requirements.',
        'We apply administrative and technical safeguards — including encryption in transit and access controls — to protect data against unauthorized access, loss or misuse.',
      ],
    },
    {
      heading: '6. Your rights',
      body: [
        'You can access and update most account information directly in your settings. You may also request correction or deletion of personal data, subject to our legal and operational obligations.',
        'To make a request, contact our Client Relations team through the channels listed below.',
      ],
    },
    {
      heading: '7. Changes and contact',
      body: [
        'We may update this policy from time to time. Material changes will be reflected by updating the date above and, where appropriate, notifying you in the platform.',
        'Questions about this policy can be sent to our Client Relations team at sandoog.net.',
      ],
    },
  ],
};

const privacyAr: LegalContent = {
  title: 'سياسة الخصوصية',
  updated: `آخر تحديث: ${EFFECTIVE_DATE_AR}`,
  intro:
    'تُشغّل صندوق ("صندوق"، "نحن") منصة صندوق سنتر، وهي منصة تجهيز وتوصيل تساعد الشركات في العراق على إدارة طلباتها ومخزونها ومدفوعاتها. توضّح هذه السياسة المعلومات التي نجمعها وكيفية استخدامها والخيارات المتاحة لك.',
  sections: [
    {
      heading: '1. المعلومات التي نجمعها',
      body: [
        'تفاصيل الحساب والعمل التي تقدّمها عند التسجيل — مثل اسم شركتك ومعلومات التواصل والفروع والمستخدمين الذين تضيفهم إلى مساحة عملك.',
        'البيانات التشغيلية التي تنشئها أو ترفعها أثناء استخدام المنصة، بما في ذلك الطلبات والمنتجات والمخزون وعناوين التوصيل ومعلومات العملاء اللازمة لإتمام تلك الطلبات.',
        'البيانات التقنية وبيانات الاستخدام التي تُنشأ تلقائياً، مثل سجلات الدخول ومعلومات الجهاز والمتصفّح وطريقة تفاعلك مع لوحة التحكم، ونستخدمها للحفاظ على أمان الخدمة وموثوقيتها.',
      ],
    },
    {
      heading: '2. كيف نستخدم المعلومات',
      body: [
        'لتشغيل المنصة: معالجة الطلبات وإتمامها، وإدارة مستودعك، وإنشاء بطاقات الشحن، واحتساب التسويات والمدفوعات.',
        'لتقديم الدعم وإرسال تحديثات الخدمة ومنع الاحتيال وإساءة الاستخدام وتأمين المنصة والامتثال لالتزاماتنا القانونية.',
        'لتحسين الخدمة وتطوير ميزات جديدة بالاعتماد على اتجاهات استخدام مُجمّعة ومجهولة الهوية.',
      ],
    },
    {
      heading: '3. بيانات العملاء التي تقدّمها',
      body: [
        'عند رفع معلومات عن عملائك (الأسماء وأرقام الهواتف والعناوين)، تبقى مسؤولاً عن تلك البيانات وعن وجود أساس قانوني لمشاركتها معنا.',
        'تعالج صندوق هذه المعلومات فقط لتقديم خدمات التجهيز والتوصيل التي تطلبها — مثل إرسال الطلبات وتوصيلها.',
      ],
    },
    {
      heading: '4. المشاركة والإفصاح',
      body: [
        'نشارك البيانات مع شركاء التوصيل واللوجستيات ومزوّدي الدفع والتسوية وموردي البنية التحتية، وذلك حصراً لتقديم الخدمة.',
        'قد نُفصح عن المعلومات عند الاقتضاء القانوني أو لحماية حقوق وسلامة وأمن صندوق وعملائنا وعملائهم. نحن لا نبيع بياناتك.',
      ],
    },
    {
      heading: '5. الاحتفاظ بالبيانات والأمان',
      body: [
        'نحتفظ بالمعلومات طالما أن حسابك نشط وبالقدر اللازم لتقديم الخدمة وحلّ النزاعات والوفاء بالمتطلبات القانونية والمحاسبية.',
        'نطبّق ضوابط إدارية وتقنية — تشمل التشفير أثناء النقل والتحكّم في الوصول — لحماية البيانات من الوصول غير المصرّح به أو فقدانها أو إساءة استخدامها.',
      ],
    },
    {
      heading: '6. حقوقك',
      body: [
        'يمكنك الوصول إلى معظم معلومات الحساب وتحديثها مباشرة من الإعدادات. كما يمكنك طلب تصحيح بياناتك الشخصية أو حذفها، وفقاً لالتزاماتنا القانونية والتشغيلية.',
        'لتقديم طلب، تواصل مع فريق علاقات العملاء عبر القنوات المذكورة أدناه.',
      ],
    },
    {
      heading: '7. التغييرات والتواصل',
      body: [
        'قد نُحدّث هذه السياسة من وقت لآخر. ستُعكس التغييرات الجوهرية بتحديث التاريخ أعلاه، وعند الاقتضاء بإشعارك داخل المنصة.',
        'يمكن إرسال الأسئلة حول هذه السياسة إلى فريق علاقات العملاء عبر sandoog.net.',
      ],
    },
  ],
};

const termsEn: LegalContent = {
  title: 'Terms of Service',
  updated: `Last updated: ${EFFECTIVE_DATE_EN}`,
  intro:
    'These Terms govern your access to and use of Sandoog Center, the fulfillment platform operated by Sandoog for businesses in Iraq. By creating an account or using the platform, you agree to these Terms.',
  sections: [
    {
      heading: '1. Accounts and eligibility',
      body: [
        'Sandoog Center is provided for business use. You must provide accurate registration details and are responsible for the activity of every user and entity you add to your workspace.',
        'You are responsible for keeping your credentials secure and for all actions taken under your account.',
      ],
    },
    {
      heading: '2. Use of the platform',
      body: [
        'Subject to these Terms, we grant you a non-exclusive, non-transferable right to use the platform to manage your orders, warehouse, fulfillment and payouts.',
        'You agree not to misuse the platform, attempt to disrupt it, access it without authorization, or use it for any unlawful purpose.',
      ],
    },
    {
      heading: '3. Merchant responsibilities',
      body: [
        'You are responsible for the accuracy of the product, order and customer information you submit, and for ensuring the goods you fulfill are lawful and permitted for delivery.',
        'Prohibited, restricted or hazardous goods may not be processed through the platform.',
      ],
    },
    {
      heading: '4. Fulfillment and delivery',
      body: [
        'Fulfillment and delivery are carried out by Sandoog and its logistics partners. Delivery timelines are provided on a best-effort basis and may be affected by factors outside our control.',
      ],
    },
    {
      heading: '5. Fees, settlements and payouts',
      body: [
        'Applicable fees, balances, statements and payouts are presented in Iraqi Dinar (IQD) within the platform.',
        'If you believe a statement or settlement is incorrect, you may raise a dispute through the platform within the period indicated; otherwise statements are treated as accepted.',
      ],
    },
    {
      heading: '6. Customer data and privacy',
      body: [
        'Your handling of customer data through the platform is also governed by our Privacy Policy. You confirm you have the right to share with us any customer information you upload.',
      ],
    },
    {
      heading: '7. Service availability',
      body: [
        'We work to keep the platform available and reliable but do not guarantee uninterrupted or error-free operation. Current service health is published on our status page.',
        'We may modify, suspend or discontinue features with reasonable notice where practicable.',
      ],
    },
    {
      heading: '8. Liability',
      body: [
        'To the maximum extent permitted by law, Sandoog is not liable for indirect, incidental or consequential losses arising from your use of the platform. The platform is provided on an "as available" basis.',
      ],
    },
    {
      heading: '9. Suspension and termination',
      body: [
        'We may suspend or terminate access if these Terms are breached or where required to protect the platform, its users or their customers. You may stop using the service at any time.',
      ],
    },
    {
      heading: '10. Governing law, changes and contact',
      body: [
        'These Terms are governed by the laws of the Republic of Iraq. We may update them from time to time, and continued use after an update constitutes acceptance.',
        'For any questions, contact our Client Relations team at sandoog.net.',
      ],
    },
  ],
};

const termsAr: LegalContent = {
  title: 'شروط الخدمة',
  updated: `آخر تحديث: ${EFFECTIVE_DATE_AR}`,
  intro:
    'تحكم هذه الشروط وصولك إلى منصة صندوق سنتر واستخدامك لها، وهي منصة التجهيز والتوصيل التي تُشغّلها صندوق للشركات في العراق. بإنشائك حساباً أو باستخدامك المنصة، فإنك توافق على هذه الشروط.',
  sections: [
    {
      heading: '1. الحسابات والأهلية',
      body: [
        'تُقدَّم منصة صندوق سنتر للاستخدام التجاري. يجب تقديم بيانات تسجيل دقيقة، وأنت مسؤول عن نشاط كل مستخدم وفرع تضيفه إلى مساحة عملك.',
        'أنت مسؤول عن الحفاظ على سرية بيانات الدخول الخاصة بك وعن جميع الإجراءات التي تُتخذ ضمن حسابك.',
      ],
    },
    {
      heading: '2. استخدام المنصة',
      body: [
        'وفقاً لهذه الشروط، نمنحك حقاً غير حصري وغير قابل للنقل لاستخدام المنصة لإدارة طلباتك ومخزونك وعمليات التجهيز والمدفوعات.',
        'توافق على عدم إساءة استخدام المنصة أو محاولة تعطيلها أو الوصول إليها دون تصريح أو استخدامها لأي غرض غير قانوني.',
      ],
    },
    {
      heading: '3. مسؤوليات التاجر',
      body: [
        'أنت مسؤول عن دقة معلومات المنتجات والطلبات والعملاء التي تقدّمها، وعن ضمان أن البضائع التي تُجهَّز قانونية ومسموح بتوصيلها.',
        'لا يجوز معالجة البضائع المحظورة أو المقيّدة أو الخطرة عبر المنصة.',
      ],
    },
    {
      heading: '4. التجهيز والتوصيل',
      body: [
        'تتم عمليات التجهيز والتوصيل عبر صندوق وشركائها في اللوجستيات. تُقدَّم مدد التوصيل على أساس بذل أفضل الجهود وقد تتأثر بعوامل خارجة عن سيطرتنا.',
      ],
    },
    {
      heading: '5. الرسوم والتسويات والمدفوعات',
      body: [
        'تُعرض الرسوم والأرصدة والكشوف والمدفوعات المعمول بها بالدينار العراقي (IQD) داخل المنصة.',
        'إذا اعتقدت أن كشفاً أو تسوية غير صحيحة، يمكنك تقديم اعتراض عبر المنصة خلال المدة المحددة؛ وإلا تُعتبر الكشوف مقبولة.',
      ],
    },
    {
      heading: '6. بيانات العملاء والخصوصية',
      body: [
        'تخضع طريقة تعاملك مع بيانات العملاء عبر المنصة أيضاً لسياسة الخصوصية الخاصة بنا. وتؤكد أن لديك الحق في مشاركة أي معلومات عملاء ترفعها إلينا.',
      ],
    },
    {
      heading: '7. توفّر الخدمة',
      body: [
        'نعمل على إبقاء المنصة متاحة وموثوقة، لكننا لا نضمن تشغيلاً متواصلاً أو خالياً من الأخطاء. تُنشر حالة الخدمة الحالية على صفحة الحالة.',
        'قد نعدّل بعض الميزات أو نوقفها مؤقتاً أو نلغيها مع إشعار معقول حيثما أمكن.',
      ],
    },
    {
      heading: '8. المسؤولية',
      body: [
        'إلى الحد الأقصى الذي يسمح به القانون، لا تتحمّل صندوق المسؤولية عن الخسائر غير المباشرة أو العرضية أو التبعية الناشئة عن استخدامك للمنصة. تُقدَّم المنصة على أساس "حسب توفّرها".',
      ],
    },
    {
      heading: '9. التعليق والإنهاء',
      body: [
        'يجوز لنا تعليق الوصول أو إنهاؤه عند مخالفة هذه الشروط أو عند الضرورة لحماية المنصة أو مستخدميها أو عملائهم. ويمكنك التوقف عن استخدام الخدمة في أي وقت.',
      ],
    },
    {
      heading: '10. القانون الحاكم والتغييرات والتواصل',
      body: [
        'تخضع هذه الشروط لقوانين جمهورية العراق. وقد نُحدّثها من وقت لآخر، ويُعدّ استمرارك في الاستخدام بعد التحديث قبولاً بها.',
        'لأي استفسار، تواصل مع فريق علاقات العملاء عبر sandoog.net.',
      ],
    },
  ],
};

const CONTENT: Record<LegalDoc, Record<SupportedLanguages, LegalContent>> = {
  privacy: {
    [SupportedLanguages.English]: privacyEn,
    [SupportedLanguages.Arabic]: privacyAr,
  },
  terms: {
    [SupportedLanguages.English]: termsEn,
    [SupportedLanguages.Arabic]: termsAr,
  },
};

export const getLegalContent = (
  doc: LegalDoc,
  language: SupportedLanguages
): LegalContent => CONTENT[doc][language] ?? CONTENT[doc][SupportedLanguages.English];
